import { Link } from 'wouter'
import { useEffect, useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { skipToken, useSuspenseQuery } from '@apollo/client'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { goodie as goodieHelpers } from '@guestgoodie/helpers'
import { Delete01Icon, FloppyDiskIcon } from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import { showToast, ToastType } from '@/features/toast'
import { Editor } from '@/features/editor'
import { GalleryUpload } from '@/features/files'
import Input from '@/components/ui/Input'
import { Seperator } from '@/components/ui/Seperator'
import Checkbox from '@/components/ui/Checkbox'
import PriceInput from '@/components/ui/PriceInput'
import { MultipleRecurrenceSelect } from '@/features/recurrence'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import Button from '@/components/ui/Button'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { graphqlToForm } from '@/features/translations'
import { useBranding } from '@/features/branding'
import { BannersField } from '@/features/banner'

import usePromotionForm, {
  FormData,
  TRANSLATABLE_FIELDS
} from '../hooks/usePromotionForm'

interface Props {
  id?: number
  goodieId?: number
  readOnly?: boolean
  onDone?: (
    promotion?: GraphQL.PromotionFragmentFragment,
    deleted?: boolean
  ) => void
}

export function PromotionForm(props: Props) {
  const { id, goodieId, onDone, readOnly = false } = props

  const { t } = useLingui()

  const { data: promoData } = useSuspenseQuery(
    AdminAPI.promotion.GET,
    id
      ? {
          variables: { id, withGallery: true, withTranslations: true }
        }
      : skipToken
  )

  const promotion = getFragment(
    SharedAPI.promotion.PROMOTION_FRAGMENT,
    promoData?.getPromotion
  )

  const { data: goodieData } = useSuspenseQuery(AdminAPI.goodie.GET, {
    variables: {
      id: goodieId ?? promotion?.goodieId ?? -1,
      withGallery: true,
      withTranslations: true
    },
    skip: !(goodieId || promotion)
  })

  const goodie = getFragment(
    SharedAPI.goodie.GOODIE_FRAGMENT,
    goodieData?.getGoodie
  )

  useEffect(() => {
    if (goodie) return

    showToast({
      id: 'goodie-not-found',
      type: ToastType.ERROR,
      message: t`Goodie or Promotion doesn't exist.`
    })

    onDone?.()
  }, [goodie, onDone, t])

  if (!goodie) return null

  return (
    <FormContent
      goodie={goodie}
      promotion={promotion}
      readOnly={readOnly}
      onDone={onDone}
    />
  )
}

interface ContentProps {
  promotion?: GraphQL.PromotionFragmentFragment
  goodie: GraphQL.GoodieFragmentFragment
  readOnly: boolean
  onDone?: (
    promotion?: GraphQL.PromotionFragmentFragment,
    deleted?: boolean
  ) => void
}

function FormContent(props: ContentProps) {
  const { promotion, goodie, onDone, readOnly } = props

  const { t } = useLingui()

  const {
    branding: { supportedLocales }
  } = useBranding()

  const maxPrice = useMemo(() => {
    const goodieType = goodieHelpers.toGoodieType(goodie)

    if (goodieType && 'price' in goodieType) return goodieType.price
    return undefined
  }, [goodie])

  const maxPriceLabel = useMemo(
    () => goodieHelpers.toPriceFromGoodieFragment(goodie),
    [goodie]
  )

  const showDiscountedPrice =
    goodie.type !== GraphQL.GoodieType.Message &&
    maxPrice != null &&
    maxPrice > 0

  const showDescription = goodie.type !== GraphQL.GoodieType.Message
  const showGallery = goodie.type !== GraphQL.GoodieType.Message

  const goodieFiles = useMemo(() => {
    return (
      goodie.gallery?.map(fileFrag =>
        getFragment(SharedAPI.file.FILE_FRAGMENT, fileFrag)
      ) ?? []
    )
  }, [goodie])

  const maxPriceExtraLabel = maxPriceLabel
    ? t`(Goodie Price: ${maxPriceLabel})`
    : ''

  const { create, update, remove, errors, progress } = usePromotionForm({
    goodieId: goodie.id,
    id: promotion?.id,
    onDone
  })

  const promotionName = promotion?.name

  const values = useMemo(() => {
    const translationsFromGoodie = graphqlToForm(
      (goodie.translations ?? [])
        .map(trans =>
          getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
        )
        .filter(trans => trans.field === 'title'),
      TRANSLATABLE_FIELDS,
      supportedLocales ?? []
    )

    const translationsFromPromotion = graphqlToForm(
      (promotion?.translations ?? []).flatMap(trans =>
        getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
      ),
      TRANSLATABLE_FIELDS,
      supportedLocales ?? []
    )

    return {
      name: promotion?.name ?? goodie.name,
      title: promotion?.title ?? goodie.title,
      discountedPrice: promotion?.discountedPrice ?? undefined,
      description: promotion?.description ?? '',
      overrideLabel: promotion?.overrideLabel ?? '',
      isActive: promotion?.isActive ?? true,
      priority: promotion?.priority ?? 0,
      recurrences:
        promotion?.recurrences?.map(rec => {
          const recurrence = {
            ...getFragment(SharedAPI.recurrence.RECURRENCE_FRAGMENT, rec)
          }
          delete recurrence.__typename

          return recurrence
        }) ?? [],
      banners:
        promotion?.banners?.map(function (bannerFragment) {
          return getFragment(SharedAPI.banner.BANNER_FRAGMENT, bannerFragment)
        }) ?? [],
      files:
        promotion?.gallery?.map(function (fileFragment) {
          return getFragment(SharedAPI.file.FILE_FRAGMENT, fileFragment)
        }) ??
        goodieFiles ??
        [],
      translations: promotion
        ? translationsFromPromotion
        : translationsFromGoodie
    } as FormData
  }, [promotion, goodie, goodieFiles, supportedLocales])

  return (
    <Form.Root<FormData>
      onSubmit={promotion ? update : create}
      serverErrors={errors}
      readOnly={readOnly}
      values={values}
    >
      <Form.Translation translatableFields={TRANSLATABLE_FIELDS}>
        <div className="flex flex-row gap-1 items-center px-2.5">
          <span className="text-regular">Goodie:</span>
          <Link
            className="text-info-500 text-regular"
            to={`/goodie/${goodie.token}`}
          >
            {goodie.name}
          </Link>
        </div>
        <div className="relative flex w-full flex-col gap-2.5 p-2.5">
          <div className="relative flex w-full flex-col gap-7">
            <Form.Field
              name="name"
              label={`${t`Name (isn't shown to Guests)`}*`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  onChange={event => onChange(event.target.value)}
                  type="text"
                  placeholder={t({
                    comment: 'Placeholder for the Name input on the Promotion',
                    message: 'e.g.: Hand-Massage 20% Discount'
                  })}
                  {...props}
                />
              )}
            </Form.Field>
            <Seperator heading={t`Public Information`} />
            <Form.Field name="isActive">
              {({ renderProps: { onChange, value, ...props } }) => (
                <Checkbox
                  onChange={() => onChange(!value)}
                  checked={value}
                  {...props}
                >
                  <Trans>Promotion is active</Trans>
                </Checkbox>
              )}
            </Form.Field>
            <Form.Field
              name="title"
              label={`${t`Title`}*`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  onChange={event => onChange(event.target.value)}
                  type="text"
                  placeholder={t({
                    comment: 'Placeholder for the Title Input on the Promotion',
                    message: 'e.g.: Hand-Massage Discount'
                  })}
                  {...props}
                />
              )}
            </Form.Field>
            {showDescription && (
              <Form.Field name="description" label={t`Description`}>
                {({ renderProps: { value, ...props } }) => (
                  <Editor
                    placeholder={t`More information for the guest about the promotion`}
                    markdown={value}
                    {...props}
                  />
                )}
              </Form.Field>
            )}
            {showGallery && (
              <Form.Field<FormData>
                name="files"
                label={`${t`Gallery`}*`}
                description={t({
                  comment:
                    'Description of the Gallery Field for the Promotion, for this they can also use the Files from the Goodie',
                  message:
                    'Images/Videos that are used by this Promotion, you can easily add images/videos already used by the goodie.'
                })}
                rules={{
                  validate: {
                    required(files) {
                      if (goodie.type === GraphQL.GoodieType.Message) return
                      if (files.length > 0) return
                      return t`This field is required`
                    }
                  }
                }}
              >
                {({
                  locale,
                  isDefaultLocale,
                  renderProps: { value, ...props }
                }) => (
                  <GalleryUpload
                    files={value}
                    quickFiles={goodieFiles}
                    accept="image/*,video/*"
                    locale={locale}
                    isDefaultLocale={isDefaultLocale}
                    {...props}
                  />
                )}
              </Form.Field>
            )}
            {showDiscountedPrice && (
              <Form.Field
                name="discountedPrice"
                label={t`Discounted Price ${maxPriceExtraLabel}`}
              >
                {({ renderProps: props }) => (
                  <PriceInput
                    placeholder={t`e.g.: 20`}
                    max={maxPrice}
                    isResetable
                    {...props}
                  />
                )}
              </Form.Field>
            )}
            <Seperator heading={t`Feed options`} />
            <Form.Field
              name="priority"
              label={t`Priority`}
              description={t`Priority can override the algorithmic sorting and put an element to the top of the feed. The higher the priority the higher it will be ordered. The default is 0.`}
              rules={{
                validate: {
                  required: Form.Validations.requiredNumber(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  onChange={event => onChange(parseInt(event.target.value))}
                  type="number"
                  {...props}
                />
              )}
            </Form.Field>
            {goodie.type !== GraphQL.GoodieType.Message && (
              <>
                <Form.Field
                  name="overrideLabel"
                  label={t`Override Label`}
                  description={t`Override the label, which is typically the price or nothing`}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: t`Can't be longer than 255 characters.`
                    }
                  }}
                >
                  {({ renderProps: { onChange, ...props } }) => (
                    <Input
                      onChange={event => onChange(event.target.value)}
                      type="text"
                      placeholder={t({
                        comment:
                          'Placeholder for the Override Label Input on the Promotion',
                        message: 'e.g.: 1 + 1 free'
                      })}
                      {...props}
                    />
                  )}
                </Form.Field>
                <Form.Field name="banners">
                  {({ renderProps: props }) => (
                    <BannersField
                      placeholder={t({
                        comment:
                          'Placeholder for the Banner input on the promotion',
                        message: 'e.g.: Guest Favorite'
                      })}
                      {...props}
                    />
                  )}
                </Form.Field>
              </>
            )}
            <Form.Data<FormData>>
              {({ recurrences }) => {
                return (
                  <Form.Field
                    name="recurrences"
                    label={t`Specific times`}
                    description={t`When enabled this will show promotions on the feed only to specific times. We show a countdown 4 hours before the specified time is expired. If this is not set, the promotion will always show.`}
                    canBeDisabled={!readOnly && recurrences.length === 0}
                    isInitialEnabled={recurrences.length > 0}
                  >
                    {({ renderProps: { value, ...props } }) => (
                      <MultipleRecurrenceSelect
                        recurrences={value}
                        hasDuration
                        {...props}
                      />
                    )}
                  </Form.Field>
                )
              }}
            </Form.Data>
          </div>
          <div className="text-center">
            <Form.Error name="root.serverError" />
          </div>
          {!readOnly && (
            <div className="flex flex-col">
              {progress != null && (
                <ProgressBar value={progress} title={t`Upload Files`} />
              )}
              <footer className="flex flex-row items-center justify-between gap-2.5 mt-2.5">
                <div>
                  {promotionName && (
                    <ConfirmDialog
                      title={t`Delete Promotion`}
                      message={t`Are you sure you want to delete the promotion with the name "${promotionName}", this cannot be reversed?`}
                      onConfirm={remove}
                      asChild
                    >
                      <Button
                        type="button"
                        variant="outline"
                        Icon={Delete01Icon}
                      >
                        <Trans>Delete</Trans>
                      </Button>
                    </ConfirmDialog>
                  )}
                </div>
                <div>
                  <Form.Submit
                    Icon={FloppyDiskIcon}
                    onlyValidWhenDirty={!!promotion}
                  >
                    <Trans>Save</Trans>
                  </Form.Submit>
                </div>
              </footer>
            </div>
          )}
        </div>
      </Form.Translation>
    </Form.Root>
  )
}
